import { ASSET_TYPE_PROPERTY } from '../../constants/PropertyConst';
import { ENVIRONMENT, PROPERTY_SERVICE_URL } from '../../env/Configurations';
import {
  abortMultipartUpload,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
} from '../../utils/commonUtils';
import { notificationManager } from '../../utils/NotificationUtils';
import { getPreSignedUrl } from '../../utils/PropertyAPIUtils';

export const getProcessedImages = async (imageList, groupId, propertyType, hotelId) => {
  let processedImageList = [];

  const imgListProcessingPromiseArr = imageList?.map(async (imgObj) => {
    return await new Promise(async (resolve, reject) => {
      const processedImageRatioSet = {
        index: imgObj?.index,
        fileName: imgObj?.fileName,
      };
      // Create a promise array to handle cropped ratios within an image
      const imgRatioProcessingPromiseArr =
        imgObj?.fileData && typeof imgObj?.fileData === 'object'
          ? Object?.keys(imgObj?.fileData)?.map((ratio) => {
              const imageFile = imgObj?.fileData[ratio]?.file;
              const imageFileSize = imageFile?.size; // In bytes
              return new Promise(async (resolveRatioImg, rejectRatioImg) => {
                try {
                  // If the image is not touched, return URL as is
                  if (!imgObj.isTouched) {
                    processedImageRatioSet[ratio] = imgObj[ratio];
                    resolveRatioImg();
                    return; // Stop further execution
                  }

                  // Process images below 2MB
                  if (imageFile && imageFileSize < 2 * 1024 * 1024) {
                    const base64 = await convertFileToBase64(imageFile);
                    const input = {
                      contentLength: imageFile?.size,
                      contentType: imageFile?.type,
                      filename: imageFile?.name,
                      type: ASSET_TYPE_PROPERTY,
                      contents: base64,
                      groupId,
                      propertyType,
                    };

                    try {
                      // Get pre-signed S3 URL
                      const res = await getPreSignedUrl(input);
                      if (res?.data?.getPreSignUrlCMS?.key) {
                        processedImageRatioSet[ratio] = res.data.getPreSignUrlCMS?.key;
                        resolveRatioImg();
                      } else {
                        rejectRatioImg(
                          `Error while uploading cropped image for ratio ${ratio}: ${imgObj?.fileName}`,
                        );
                      }
                    } catch (error) {
                      if (ENVIRONMENT === 'dev') {
                        console.log(error?.message, 'error');
                      }
                      notificationManager('danger', 'Failed to process cropped image for ratio');
                      rejectRatioImg(
                        `Failed to process cropped image for ratio ${ratio}: ${error?.message}`,
                      );
                    }
                  } else {
                    const totalChunks = calculateTotalChunks(imageFileSize);
                    let uploadId = null;

                    const fileChecksum = await calculateFileChecksum(imageFile);
                    const fileSize = imageFile.size;

                    try {
                      const startUploadResponse = await fetch(
                        `${PROPERTY_SERVICE_URL}/startMultipartUpload`,
                        {
                          method: 'POST',
                          body: JSON.stringify({
                            groupId,
                            type: ASSET_TYPE_PROPERTY,
                            contentType: imageFile?.type,
                            filename: imgObj?.fileName,
                            hotelId,
                            propertyType,
                            IsCMSRequest: true,
                            fileSize,
                            fileChecksum,
                          }),
                        },
                      );

                      const chunkData = await startUploadResponse.json();
                      uploadId = chunkData?.data?.key;

                      try {
                        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                          const start = chunkIndex * CHUNK_SIZE;
                          const end = Math.min(start + CHUNK_SIZE, imageFile.size);
                          const fileChunk = imageFile.slice(start, end);

                          const base64Chunk = await convertFileToBase64(fileChunk);
                          const response = await fetch(
                            `${PROPERTY_SERVICE_URL}/uploadMultipartChunk`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                groupId,
                                hotelId,
                                uploadId,
                                sequenceNumber: chunkIndex + 1,
                                contents: base64Chunk,
                              }),
                            },
                          );

                          const responseData = await response.json();

                          // Optional: Handle response status if needed
                          if (!response.ok) {
                            throw new Error(`Upload failed: ${responseData.message}`);
                          }
                        }

                        const completeResponse = await completeMultipartUpload(
                          groupId,
                          hotelId,
                          uploadId,
                        );

                        if (
                          completeResponse?.status === 'ok' &&
                          completeResponse?.data?.status === true
                        ) {
                          processedImageRatioSet[ratio] = completeResponse?.data?.message;
                          resolveRatioImg();
                        } else {
                          console.error('File upload failed:', completeResponse);
                        }
                      } catch (error) {
                        if (ENVIRONMENT === 'dev') {
                          console.log(error?.message, 'error');
                        }
                        notificationManager('danger', 'Error processing image ratio');
                        if (error && uploadId) {
                          await abortMultipartUpload(uploadId, groupId, hotelId);
                        }
                      }
                    } catch (error) {
                      rejectRatioImg(error);
                      if (ENVIRONMENT === 'dev') {
                        console.log(error?.message, 'error');
                      }
                      notificationManager('danger', 'Error processing image ratio');
                    }
                  }
                } catch (error) {
                  if (ENVIRONMENT === 'dev') {
                    console.log(error?.message, 'error');
                  }
                  notificationManager('danger', 'Error processing image ratio');
                  rejectRatioImg(error);
                }
              });
            })
          : [];

      try {
        await Promise.all(imgRatioProcessingPromiseArr);
        resolve(processedImageRatioSet);
      } catch (error) {
        reject(error);
        if (ENVIRONMENT === 'dev') {
          console.log(error?.message, 'error');
        }
        notificationManager('danger', 'Error processing image ratio');
      }
    });
  });

  try {
    processedImageList = await Promise.all(imgListProcessingPromiseArr);
    return processedImageList;
  } catch (error) {
    if (ENVIRONMENT === 'dev') {
      console.log(error?.message, 'error');
    }
    notificationManager('danger', 'Error processing image');
    return error;
  }
};
